import {StockLevelOverwrite} from '@domain/material-storage'
import {StockDevelopmentData} from '@domain/schedule'
import {formatLocalizedDateMonth, formatLocalizedDateWithShortYear} from '@hconnect/uikit'
import {useTheme} from '@mui/material'
import {calculateNiceAxisSteps, useDateScale, useLinearScale} from '@widgets/common-d3-chart'
import {Moment} from 'moment-timezone'
import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {STOCK_CARD_WIDGET_NAMESPACE} from '../../consts'
import {StockDevelopmentTooltipContent} from '../StockDevelopmentTooltipContent'
import {DatetimeValue, StockDevelopmentChartConfig} from '../types'

import {useCurrentTime} from './useCurrentTime'
import {useTargetLevelsDaily} from './useMinTargetLevelsDaily'

const INDICATOR_SIZE = 2

export function useStockDevelopmentChartConfigData({
  stockData,
  stockDataSecondary,
  listOfDays,
  timezoneId,
  minTargetLevels,
  storageCapacity,
  height,
  width
}: {
  stockData: StockDevelopmentData & {
    stockOverwrites?: StockLevelOverwrite[]
  }
  stockDataSecondary?: StockDevelopmentData & {
    stockOverwrites?: StockLevelOverwrite[]
  }
  listOfDays: Moment[]
  timezoneId: string
  minTargetLevels?: {weekend: number; week: number}
  storageCapacity?: number
  height: number
  width: number
}) {
  const {
    i18n: {language}
  } = useTranslation(STOCK_CARD_WIDGET_NAMESPACE)
  const {spacing} = useTheme()
  const {stockLevelHourly} = stockData
  const secondaryStockLevelHourly: Record<string, number> | undefined =
    stockDataSecondary?.stockLevelHourly

  const stockDevelopmentValues = Object.values(stockLevelHourly)
  const secondaryStockDevelopmentValues = Object.values(secondaryStockLevelHourly || {})
  const maxStockValue: number = Math.max(
    ...stockDevelopmentValues,
    ...secondaryStockDevelopmentValues
  )
  const minStockValue: number = Math.min(
    ...stockDevelopmentValues,
    ...secondaryStockDevelopmentValues
  )
  const {steps: verticalSteps} = calculateNiceAxisSteps({
    numberOfSteps: 5,
    maxValue: Math.max(
      maxStockValue,
      ...(minTargetLevels ? [minTargetLevels.week, minTargetLevels.weekend] : []),
      ...(storageCapacity ? [storageCapacity] : [])
    ),
    minValue: Math.min(minStockValue, 0)
  })
  const maxDomainValue = verticalSteps[verticalSteps.length - 1]
  const minDomainValue = Math.min(0, minStockValue)

  const labelSteps = [...listOfDays].map((day) => day.clone().startOf('day'))

  // extract number of px from spacing
  const INCOMING_ORDER_DIAMETER = parseInt(spacing(INDICATOR_SIZE))

  const yScale = useLinearScale({
    domain: [minDomainValue, maxDomainValue],
    range: [height - INCOMING_ORDER_DIAMETER, INCOMING_ORDER_DIAMETER]
  })

  const xScale = useDateScale({
    domain: [listOfDays[0], listOfDays[listOfDays.length - 1]],
    range: [0, width]
  })

  const minTargetLevelDatetimeValues = useTargetLevelsDaily({
    minTargetLevels,
    listOfDays,
    timezoneId
  })

  const maxMinTargetLevelDatetimeValues = Math.max(
    ...(minTargetLevelDatetimeValues || []).map(({value}) => value)
  )
  const minTargetLevelBaseValue = Math.min(
    minStockValue,
    ...(minTargetLevelDatetimeValues || []).map(({value}) => value)
  )

  const {stockOverwrites} = stockData

  const formatTooltipContent = useCallback(
    (datetimeValue: DatetimeValue) => (
      <StockDevelopmentTooltipContent
        canCreateStockOverwrite={false}
        datetimeValue={datetimeValue}
        stockOverwrites={stockOverwrites || []}
      />
    ),
    [stockOverwrites]
  )

  const currentTime = useCurrentTime({timezoneId})
  const formatDatetime = useCallback(
    (datetime: Moment): string => {
      const startOfChart = listOfDays[0].clone()

      if (datetime.isBefore(startOfChart)) {
        return ''
      }

      if (datetime.isSame(startOfChart)) {
        return formatLocalizedDateWithShortYear(datetime, language)
      }

      return formatLocalizedDateMonth(datetime, language)
    },
    [listOfDays, language]
  )

  return {
    yScale,
    xScale,
    timezoneId,
    verticalSteps,
    labelSteps,
    minStockValue,
    maxMinTargetLevelDatetimeValues,
    minTargetLevelBaseValue,
    formatTooltipContent,
    formatDatetime,
    currentTime
  } as StockDevelopmentChartConfig
}
